import { useState } from 'react';
import { X, Send, Car, Plane, Ship } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { usePublicAuth } from '../../contexts/PublicAuthContext.jsx';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';

const formatRoute = (trip, t) => {
  const originCity = trip.originCity || trip.originDetails?.city;
  const originCountry = trip.originCountry || trip.originDetails?.country;
  const destinationCity = trip.destinationCity || trip.destinationDetails?.city;
  const destinationCountry =
    trip.destinationCountry || trip.destinationDetails?.country;

  return `${originCity || t('home.from')}, ${originCountry || ''} → ${
    destinationCity || t('home.to')
  }, ${destinationCountry || ''}`;
};

const formatDeparture = (trip, t) => {
  if (!trip.departureDate) return t('tripCard.scheduleTbd');
  if (trip.departureTime) {
    return `${trip.departureDate} • ${trip.departureTime}`;
  }
  return trip.departureDate;
};

const formatPrice = (trip, t) => {
  const currency = trip.currency || 'USD';
  if (trip.pricingMode === 'perKg' && trip.pricePerKg) {
    return `${currency} ${trip.pricePerKg}/${trip.spaceUnit || 'kg'}`;
  }
  if (trip.pricingMode === 'perUnit' && trip.pricePerUnit) {
    return `${currency} ${trip.pricePerUnit}/${trip.spaceUnit || 'unit'}`;
  }
  if (trip.pricingMode === 'flat' && trip.flatPrice) {
    return `${currency} ${trip.flatPrice} flat`;
  }
  if (trip.flatPrice) {
    return `${currency} ${trip.flatPrice}`;
  }
  return t('tripCard.contactForPrice');
};

const deliveryMethods = t => [
  {
    label: t('travelerTripRequests.byRail'),
    value: 'byRail',
    icon: Ship,
  },
  {
    label: t('travelerTripRequests.byAir'),
    value: 'byAir',
    icon: Plane,
  },
  {
    label: t('travelerTripRequests.byRoad'),
    value: 'byRoad',
    icon: Car,
  },
];

export function SendOfferModal({ visible, onClose, trip }) {
  const { user } = usePublicAuth();
  const [formData, setFormData] = useState({
    deliveryMethod: '',
    price: '',
    currency: 'USD',
    notes: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeliveryMethodModal, setShowDeliveryMethodModal] = useState(false);
  const { t } = useTranslation();

  if (!visible || !trip) return null;

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    if (!formData.deliveryMethod) {
      alert(t('sendOfferModal.validation.deliveryMethodRequired'));
      return false;
    }
    if (!formData.price || parseFloat(formData.price) <= 0) {
      alert(t('sendOfferModal.validation.priceRequired'));
      return false;
    }
    if (!formData.currency) {
      alert(t('sendOfferModal.validation.currencyRequired'));
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    if (!user) {
      alert(t('sendOfferModal.validation.loginRequired'));
      return;
    }

    setIsSubmitting(true);
    try {
      const tripId = trip.id;
      const tripOwnerId = trip.createdBy || trip.ownerId || trip.userId;

      if (!tripId) {
        alert(t('sendOfferModal.error.tripIdMissing'));
        return;
      }

      if (!tripOwnerId) {
        alert(t('sendOfferModal.error.tripOwnerMissing'));
        return;
      }

      const route = formatRoute(trip, t);
      const departure = formatDeparture(trip, t);

      const requestData = {
        tripId: tripId,
        tripOwnerId: tripOwnerId,
        requesterId: user.uid,
        requesterName:
          user.displayName || user.email?.split('@')[0] || t('common.unknownUser'),
        requesterEmail: user.email,

        // Add trip type to request
        tripType: trip.tripType || 'carrying',

        // Item details - for carrying trips, we use the trip's item description
        itemTitle: trip.itemDescription || t('sendOfferModal.itemFallback'),
        itemDescription: trip.itemDescription || t('sendOfferModal.itemFallback'),
        weight: parseFloat(trip.availableSpace) || 0,
        spaceUnit: trip.spaceUnit || 'kg',
        materialType: trip.materialType || 'general',
        notes: formData.notes.trim(),

        // Trip details (for reference)
        route: route,
        departure: departure,

        // Pricing - for carrying trips, traveler offers their asking price
        price: parseFloat(formData.price),
        estimatedPrice: parseFloat(formData.price),
        currency: formData.currency || 'USD',

        // Delivery method - traveler selects
        deliveryMethod: formData.deliveryMethod,

        // Status
        status: 'pending',

        // Timestamps
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await addDoc(collection(db, 'requests'), requestData);

      // Reset form
      setFormData({
        deliveryMethod: '',
        price: '',
        currency: 'USD',
        notes: '',
      });

      alert(t('sendOfferModal.success'));
      onClose();
    } catch (error) {
      console.error('Error sending offer:', error);
      alert(t('sendOfferModal.error.generic'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      deliveryMethod: '',
      price: '',
      currency: 'USD',
      notes: '',
    });
    setShowDeliveryMethodModal(false);
    onClose();
  };

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center ${
        visible ? 'block' : 'hidden'
      }`}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-slate-800">
          <button
            onClick={handleClose}
            className="text-gray-600 dark:text-slate-400 font-semibold hover:text-gray-900 dark:hover:text-white transition"
            disabled={isSubmitting}
          >
            {t('common.cancel')}
          </button>
          <h2 className="text-2xl font-black text-gray-900 dark:text-white">
            {t('sendOfferModal.title')}
          </h2>
          <button
            onClick={handleSubmit}
            disabled={
              isSubmitting ||
              !formData.deliveryMethod ||
              !formData.price ||
              parseFloat(formData.price) <= 0
            }
            className="bg-blue-600 dark:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-700 dark:hover:bg-blue-800 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? t('sendOfferModal.sending') : t('sendOfferModal.send')}
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Sender Info */}
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border-2 border-yellow-200 dark:border-yellow-800 rounded-xl p-4 mb-6">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
              {trip.createdByName || t('sendOfferModal.senderFallback')}
            </h3>
            <p className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-1">
              {formatRoute(trip)}
            </p>
            <p className="text-sm text-gray-600 dark:text-slate-400">
              {formatDeparture(trip)}
            </p>
          </div>

          {/* Form */}
          <div className="space-y-6">
            {/* Delivery Method */}
            <div>
              <label className="block text-sm font-bold text-gray-900 dark:text-white mb-2">
                {t('sendOfferModal.deliveryMethodLabel')}{' '}
                <span className="text-red-500">*</span>
              </label>
              <button
                type="button"
                onClick={() => setShowDeliveryMethodModal(true)}
                className="w-full border-2 border-gray-300 dark:border-slate-700 rounded-xl px-4 py-3 text-left focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-yellow-500 flex items-center justify-between bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
              >
                <span
                  className={
                    formData.deliveryMethod
                      ? 'text-gray-900 dark:text-white'
                      : 'text-gray-400 dark:text-slate-500'
                  }
                >
                  {formData.deliveryMethod
                    ? deliveryMethods(t).find(
                        method => method.value === formData.deliveryMethod,
                      )?.label
                    : t('sendOfferModal.selectDeliveryMethodPlaceholder')}
                </span>
                <span className="text-gray-400 dark:text-slate-500">▼</span>
              </button>
            </div>

            {/* My Asking Price */}
            <div>
              <label className="block text-sm font-bold text-gray-900 dark:text-white mb-2">
                {t('sendOfferModal.askingPrice')}{' '}
                <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  placeholder={t('sendOfferModal.pricePlaceholder')}
                  value={formData.price}
                  onChange={e => handleInputChange('price', e.target.value)}
                  min="0"
                  step="0.01"
                  className="flex-1 border-2 border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-yellow-500 placeholder:text-gray-400 dark:placeholder:text-slate-500"
                />
                <span className="text-sm font-semibold text-gray-600 dark:text-slate-400">
                  {formData.currency}
                </span>
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-bold text-gray-900 dark:text-white mb-2">
                {t('sendOfferModal.notesLabel')}
              </label>
              <textarea
                placeholder={t('sendOfferModal.notesPlaceholder')}
                value={formData.notes}
                onChange={e => handleInputChange('notes', e.target.value)}
                maxLength={500}
                rows={3}
                className="w-full border-2 border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-yellow-500 resize-none placeholder:text-gray-400 dark:placeholder:text-slate-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Delivery Method Modal */}
      {showDeliveryMethodModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => setShowDeliveryMethodModal(false)}
          />
          <div className="relative bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-slate-800">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                {t('sendOfferModal.selectDeliveryMethodTitle')}
              </h3>
              <button
                onClick={() => setShowDeliveryMethodModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition"
              >
                <X className="h-5 w-5 text-gray-600 dark:text-slate-400" />
              </button>
            </div>
            <div className="p-4">
              {deliveryMethods(t).map(method => {
                const Icon = method.icon;
                return (
                  <button
                    key={method.value}
                    type="button"
                    onClick={() => {
                      handleInputChange('deliveryMethod', method.value);
                      setShowDeliveryMethodModal(false);
                    }}
                    className="w-full flex items-center gap-3 p-4 rounded-xl hover:bg-gray-50 dark:hover:bg-slate-800 transition text-left border-2 border-transparent hover:border-gray-200 dark:hover:border-slate-700"
                  >
                    <Icon className="h-5 w-5 text-gray-600 dark:text-slate-400" />
                    <span className="text-base font-medium text-gray-900 dark:text-white">
                      {method.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
